import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import CryptoJS from "crypto-js";
import { getDecryptedUserFromCookie } from "../Components/utils/getCookie";
import { permissionForPath } from "../config/permissionMenuMap";
import { fetchMyPermissions } from "../services/rbacApi";

const STORAGE_KEY = "staff_permissions";

const PermissionContext = createContext({
  permissions: [],
  loading: false,
  isStaff: true,
  isAdmin: false,
  hasPermission: () => true,
  hasAnyPermission: () => true,
  canAccessPath: () => true,
  setPermissions: () => {},
  clearPermissions: () => {},
  storeEncryptedPermissions: () => {},
  refreshPermissions: () => {},
});

function decryptPermissionsBlob(encrypted) {
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, process.env.REACT_APP_CRYPTO_SECRET);
    const raw = bytes.toString(CryptoJS.enc.Utf8);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed?.permissions)) return parsed.permissions;
    if (Array.isArray(parsed)) return parsed;
    return null;
  } catch {
    return null;
  }
}

function loadStoredPermissions() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function PermissionProvider({ children }) {
  const user = getDecryptedUserFromCookie();
  const userType = String(user?.type ?? "staff").toLowerCase();
  const isStaff = userType === "staff";
  const isAdmin = !isStaff;

  const [permissions, setPermissionsState] = useState(() => loadStoredPermissions());
  const [loading, setLoading] = useState(false);

  const setPermissions = useCallback((next) => {
    const list = Array.isArray(next) ? next : [];
    setPermissionsState(list);
    if (list.length) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const clearPermissions = useCallback(() => {
    setPermissionsState([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const storeEncryptedPermissions = useCallback((encrypted) => {
    const list = decryptPermissionsBlob(encrypted);
    if (list?.length) setPermissions(list);
  }, [setPermissions]);

  const refreshPermissions = useCallback(async () => {
    const loggedIn = getDecryptedUserFromCookie();
    if (!loggedIn?.id && !loggedIn?.emplyee_ID) return;
    setLoading(true);
    try {
      const payload = await fetchMyPermissions();
      if (Array.isArray(payload?.permissions)) {
        setPermissions(payload.permissions);
      }
    } catch {
      /* keep cached permissions */
    } finally {
      setLoading(false);
    }
  }, [setPermissions]);

  useEffect(() => {
    refreshPermissions();
  }, [refreshPermissions]);

  const hasPermission = useCallback(
    (slug) => {
      if (!slug) return true;
      return permissions.includes(slug);
    },
    [permissions]
  );

  const hasAnyPermission = useCallback(
    (slugs = []) => {
      if (!slugs.length) return true;
      return slugs.some((s) => permissions.includes(s));
    },
    [permissions]
  );

  const canAccessPath = useCallback(
    (pathname) => {
      const required = permissionForPath(pathname);
      if (!required) return true;
      return hasPermission(required);
    },
    [hasPermission]
  );

  const value = useMemo(
    () => ({
      permissions,
      loading,
      isStaff,
      isAdmin,
      hasPermission,
      hasAnyPermission,
      canAccessPath,
      setPermissions,
      clearPermissions,
      storeEncryptedPermissions,
      refreshPermissions,
    }),
    [
      permissions,
      loading,
      isStaff,
      isAdmin,
      hasPermission,
      hasAnyPermission,
      canAccessPath,
      setPermissions,
      clearPermissions,
      storeEncryptedPermissions,
      refreshPermissions,
    ]
  );

  return (
    <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>
  );
}

export function usePermissions() {
  return useContext(PermissionContext);
}

export default PermissionContext;
